import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import { CircularProgress, Typography } from '@mui/material';

import { SEARCH_VIEW_COOKIE } from 'app/constants';
import SearchStore from 'app/store/SearchStore';
import useStores from 'hooks/use-stores';
import EmptyBox from 'main/components/panel/EmptyBox';
import Speller from 'main/components/search/Speller';
import { SearchType } from 'main/components/SearchBar';
import { ElasticQueryBuilder } from 'modules/elastic';
import Occurrences from 'pages/Search/components/Database/components/Occurrences';
import UpgradeLicense from 'pages/Search/components/Database/components/UpgradeLicense';
import Paginator from 'pages/Search/components/Paginator';
import VulnerabilitiesList from 'pages/Search/components/VulnerabilitiesList';
import { getCookie } from 'utils/cookies';
import isBrowser from 'utils/is-browser';
import { stringifyQuery } from 'utils/stringify-query';

import useSortingState from './hooks/use-sorting-state';
import useStyles from './styles';

interface Props {
    onPageChange: (parameters: object) => void;
}

const SearchResults: React.FC<Props> = ({ onPageChange }) => {
    const location = useLocation();
    const { classes } = useStyles();
    const { searchStore } = useStores();
    const sorting = useSortingState();

    useMemo(() => {
        if (isBrowser()) {
            const view = getCookie(SEARCH_VIEW_COOKIE);
            if (view) {
                searchStore.setView(view);
            }
        }
    }, [searchStore]);

    const skip = parseInt(new URLSearchParams(location.search).get('skip') || '') || 0;

    const getPaginationHref = useCallback(
        (page) => {
            if (location.pathname.startsWith('/search/tags')) {
                return stringifyQuery(location.pathname, {
                    skip: (page - 1) * searchStore.pageSize || '',
                    size: searchStore.pageSize === SearchStore.DEFAULT_PAGE_SIZE ? '' : searchStore.pageSize
                });
            } else {
                return stringifyQuery('/search', {
                    query: searchStore.query,
                    software: searchStore.software,
                    version: searchStore.version,
                    state: ElasticQueryBuilder.stateToString(searchStore.requestState),
                    skip: (page - 1) * searchStore.pageSize || '',
                    size: searchStore.pageSize === SearchStore.DEFAULT_PAGE_SIZE ? '' : searchStore.pageSize
                });
            }
        },
        [
            location.pathname,
            searchStore.pageSize,
            searchStore.query,
            searchStore.requestState,
            searchStore.software,
            searchStore.version
        ]
    );

    const handlePaginationChange = useCallback(
        ({ skip, size }) => {
            const query = new URLSearchParams(window.location.search).get('query')?.trim() || '';
            const software = new URLSearchParams(window.location.search).get('software')?.trim() || '';
            const version = new URLSearchParams(window.location.search).get('version')?.trim() || '';
            const state = new URLSearchParams(window.location.search).get('state')?.trim() || '';

            onPageChange({
                query,
                software,
                version,
                state: ElasticQueryBuilder.stringToState(state),
                skip: skip || '',
                size: size === SearchStore.DEFAULT_PAGE_SIZE ? '' : size
            });
        },
        [onPageChange]
    );

    const handlePageChange = useCallback(
        (page) => {
            handlePaginationChange({ skip: (page - 1) * searchStore.pageSize, size: searchStore.pageSize });
        },
        [handlePaginationChange, searchStore.pageSize]
    );

    const handleRowsPerPageChange = useCallback(
        (rowsPerPage) => {
            handlePaginationChange({ skip: 0, size: rowsPerPage });
        },
        [handlePaginationChange]
    );

    const handleSpellerClick = useCallback(
        (query) => onPageChange({ query, software: '', version: '', skip: 0 }),
        [onPageChange]
    );

    if (searchStore.isLoading) {
        return (
            <EmptyBox>
                <CircularProgress size={64} thickness={2} />
            </EmptyBox>
        );
    }

    if (isEmpty(searchStore.data)) {
        if (searchStore.type === SearchType.product && !searchStore.productSearchEnabled) {
            return <UpgradeLicense />;
        }
        return (
            <>
                <Speller
                    show={searchStore.type === SearchType.simple}
                    query={searchStore.lastSearchQuery}
                    onSpellClick={handleSpellerClick}
                />
                <EmptyBox>
                    <Typography className={classes.notFound}>{'Nothing found'}</Typography>
                </EmptyBox>
            </>
        );
    }

    return (
        <>
            <VulnerabilitiesList
                total={searchStore.total}
                items={searchStore.data}
                exact={searchStore.exact}
                sort={sorting}
            >
                {(content) => (
                    <>
                        {isEmpty(searchStore.occurrences) ? null : <Occurrences data={searchStore.occurrences} />}
                        {content}
                    </>
                )}
            </VulnerabilitiesList>
            <Paginator
                className={classes.paginator}
                page={Math.floor(skip / searchStore.pageSize) + 1}
                siblingCount={2}
                rowsPerPage={searchStore.pageSize}
                count={searchStore.total}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                getPaginationHref={getPaginationHref}
            />
        </>
    );
};

export default observer(SearchResults);
